import React from 'react';

const AuthenticationFlow = () => {
  const loginFlow = [
    { step: 1, title: 'User Submits Credentials', detail: 'POST /api/v1/login with {username, password} JSON body', component: 'Frontend (Login.js)' },
    { step: 2, title: 'Nginx Rate Limit Check', detail: 'Nginx limit_req zone checks: max 10 req/min per IP on /api/v1/login', component: 'Nginx (gosmpp.conf)' },
    { step: 3, title: 'App Rate Limit Check', detail: 'LoginRateLimitMiddleware checks IP against in-memory tracker. 5 failed attempts → 15-min lockout', component: 'middleware/rate_limiter.go' },
    { step: 4, title: 'Request Binding', detail: 'Gin binds JSON to LoginRequest struct. Generic error returned if invalid (no field names leaked)', component: 'api/auth_handler.go' },
    { step: 5, title: 'User Lookup', detail: 'SELECT from users WHERE username=? (parameterized query, no SQL injection)', component: 'api/auth_handler.go' },
    { step: 6, title: 'Password Verification', detail: 'bcrypt.CompareHashAndPassword() — constant-time comparison, no timing attacks', component: 'auth/password.go' },
    { step: 7, title: 'Account Status Check', detail: 'Verify user.is_active=TRUE. Disabled accounts get 403 Forbidden', component: 'api/auth_handler.go' },
    { step: 8, title: 'JWT Token Generation', detail: 'Generate HS256 JWT with {user_id, username, role, exp: 30min, iat: now}', component: 'auth/jwt.go' },
    { step: 9, title: 'Redis Session Creation', detail: 'Create session:{user_id}:{token_id} with 30-min TTL. Also create token_mapping:{jwt} → token_id', component: 'auth/session.go' },
    { step: 10, title: 'Audit Log', detail: 'INSERT into auth_audit_log with user_id, event_type=login, status=success, IP, user_agent', component: 'api/auth_handler.go' },
    { step: 11, title: 'Response', detail: 'Return {token, user (without password_hash), entities[]} as JSON', component: 'api/auth_handler.go' },
    { step: 12, title: 'Frontend Storage', detail: 'Token stored in localStorage. User data and entities cached locally', component: 'Frontend (AuthContext.js)' },
  ];

  const sessionFlow = [
    { title: 'Every API Request', detail: 'Frontend sends Authorization: Bearer {jwt} header with every request' },
    { title: 'JWT Validation', detail: 'AuthMiddleware extracts token, checks blacklist, validates signature and expiry' },
    { title: 'Redis Session Check', detail: 'Look up token_mapping:{jwt} → get token_id → check session:{user_id}:{token_id} exists' },
    { title: 'Sliding TTL Refresh', detail: 'On valid session, refresh both session and token_mapping TTL to 30 minutes' },
    { title: 'Context Injection', detail: 'Set user_id, username, role in Gin context for downstream handlers' },
    { title: 'Redis Fallback', detail: 'If Redis is unavailable, fall back to JWT-only validation (no session check)' },
  ];

  const securityFeatures = [
    {
      category: 'Password Security',
      items: [
        { name: 'bcrypt Hashing', detail: 'Cost factor 10, salted, constant-time comparison' },
        { name: 'Complexity Rules', detail: 'Min 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 special character' },
        { name: 'No Plaintext Storage', detail: 'Only bcrypt hash stored in password_hash column' },
      ]
    },
    {
      category: 'Token Security',
      items: [
        { name: 'JWT HS256', detail: '64-char cryptographically random secret (openssl rand -base64 48)' },
        { name: '30-min Expiry', detail: 'Short-lived tokens with sliding session extension' },
        { name: 'Token Blacklisting', detail: 'On logout, token added to Redis blacklist (token_blacklist:{jwt})' },
        { name: 'Dual Validation', detail: 'JWT signature + Redis session must both be valid' },
      ]
    },
    {
      category: 'Brute Force Protection',
      items: [
        { name: 'IP Rate Limiting', detail: '5 failed attempts per 15 min → 15-min IP lockout (in-memory)' },
        { name: 'Nginx Rate Limit', detail: '10 req/min per IP on /api/v1/login with burst=5' },
        { name: 'Remaining Attempts Warning', detail: 'Shows "X attempts remaining" when < 4 left' },
        { name: 'Audit Logging', detail: 'Every login attempt logged with IP, user agent, status' },
      ]
    },
    {
      category: 'HTTP Security Headers',
      items: [
        { name: 'X-Frame-Options', detail: 'SAMEORIGIN — prevents clickjacking' },
        { name: 'X-Content-Type-Options', detail: 'nosniff — prevents MIME type sniffing' },
        { name: 'X-XSS-Protection', detail: '1; mode=block — browser XSS filter' },
        { name: 'Strict-Transport-Security', detail: 'max-age=31536000 — forces HTTPS for 1 year' },
        { name: 'Referrer-Policy', detail: 'strict-origin-when-cross-origin' },
        { name: 'Permissions-Policy', detail: 'camera=(), microphone=(), geolocation=()' },
      ]
    },
    {
      category: 'API Key Authentication',
      items: [
        { name: 'Header or Query', detail: 'X-API-KEY header, X-API-Key header, or key= query param' },
        { name: 'Database Validation', detail: 'API key looked up in users table WHERE api_key=? AND is_active=TRUE' },
        { name: 'Masked Logging', detail: 'API keys logged as abcd****wxyz (first 4 + last 4 chars only)' },
      ]
    },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Authentication & Security</h1>
          <p className="mt-1 text-gray-600">Login flow, JWT lifecycle, session management, and security measures</p>
        </div>
        <div className="no-print flex gap-2">
          <button onClick={() => window.history.back()} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">← Back</button>
          <button onClick={() => window.print()} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">🖨️ Print</button>
        </div>
      </div>

      {/* Login Flow */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Login Flow (Step by Step)</h2>
        <div className="space-y-3">
          {loginFlow.map((step) => (
            <div key={step.step} className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {step.step}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900 text-sm">{step.title}</span>
                  <code className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{step.component}</code>
                </div>
                <p className="text-sm text-gray-600 mt-0.5">{step.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Session Lifecycle */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 print-break">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Session Lifecycle (Per Request)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sessionFlow.map((item, i) => (
            <div key={i} className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
                <span className="font-semibold text-gray-900 text-sm">{item.title}</span>
              </div>
              <p className="text-xs text-gray-600 ml-8">{item.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Logout Flow */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Logout Flow</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[
            { step: 1, title: 'POST /api/v1/auth/logout', detail: 'Frontend sends logout request with current JWT' },
            { step: 2, title: 'Blacklist Token', detail: 'Add token to Redis blacklist: token_blacklist:{jwt} with remaining TTL' },
            { step: 3, title: 'Invalidate Session', detail: 'Delete session:{user_id}:{token_id} and token_mapping:{jwt} from Redis' },
            { step: 4, title: 'Clear Frontend', detail: 'Remove token, user, entities from localStorage. Redirect to /login' },
          ].map((item) => (
            <div key={item.step} className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold">{item.step}</span>
                <span className="font-semibold text-gray-900 text-sm">{item.title}</span>
              </div>
              <p className="text-xs text-gray-600 ml-8">{item.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Security Features */}
      <div className="print-break">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Security Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {securityFeatures.map((cat, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-600" />
                {cat.category}
              </h3>
              <div className="space-y-2">
                {cat.items.map((item, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <span className="text-green-500 mt-0.5 flex-shrink-0 text-sm">✓</span>
                    <div>
                      <span className="text-sm font-medium text-gray-800">{item.name}</span>
                      <span className="text-sm text-gray-500"> — {item.detail}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Redis Key Patterns */}
      <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Redis Key Patterns</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="py-2 pr-4 font-semibold text-gray-700">Key Pattern</th>
                <th className="py-2 pr-4 font-semibold text-gray-700">Value</th>
                <th className="py-2 pr-4 font-semibold text-gray-700">TTL</th>
                <th className="py-2 font-semibold text-gray-700">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ['session:{user_id}:{token_id}', 'JSON (SessionData)', '30 min (sliding)', 'Active user session with login time, last activity'],
                ['token_mapping:{jwt_string}', 'token_id (UUID)', '30 min (sliding)', 'Maps JWT token to session token_id for lookup'],
                ['token_blacklist:{jwt_string}', '"1"', 'Remaining JWT TTL', 'Blacklisted tokens from logout (prevents reuse)'],
                ['user_plan:{user_id}', 'JSON (UserPlan)', '5 min', 'Cached routing plan for SMS delivery'],
                ['dashboard:{user_id}', 'JSON (counters)', '5 min', 'Cached dashboard statistics'],
              ].map((row, i) => (
                <tr key={i}>
                  <td className="py-2 pr-4 font-mono text-xs text-blue-700">{row[0]}</td>
                  <td className="py-2 pr-4 text-gray-600 text-xs">{row[1]}</td>
                  <td className="py-2 pr-4 text-gray-600 text-xs">{row[2]}</td>
                  <td className="py-2 text-gray-600 text-xs">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationFlow;
